import React, { useEffect, useState } from 'react';
import { Download, CheckCircle2, RotateCcw, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  recuentosAPI, ESTADOS, formatFechaHora, fmtNum, downloadBlob, filenameFromHeaders, errorMsg,
} from '@/lib/recuentos';

const Diff = ({ v }) => {
  if (v === null || v === undefined) return <span className="text-slate-300">—</span>;
  const r = Math.round(v * 1000) / 1000;
  const cls = r > 0 ? 'text-green-600' : r < 0 ? 'text-red-600' : 'text-slate-500';
  return <span className={cls}>{r > 0 ? '+' : ''}{fmtNum(r)}</span>;
};

const ItemsTable = ({ items, showDiff }) => (
  <table className="w-full text-sm" data-testid="detalle-items-table">
    <thead className="bg-slate-50 text-slate-600 sticky top-0">
      <tr>
        <th className="px-3 py-2 text-left">Ingrediente</th>
        <th className="px-3 py-2 text-left">Unidad</th>
        <th className="px-3 py-2 text-right">Cantidad</th>
        {showDiff && <th className="px-3 py-2 text-right">Anterior</th>}
        {showDiff && <th className="px-3 py-2 text-right">Diferencia</th>}
      </tr>
    </thead>
    <tbody>
      {items.map((i) => (
        <tr key={i.ingrediente_id} className="border-t">
          <td className="px-3 py-1.5"><div className="text-slate-800">{i.nombre_justo}</div><div className="text-[11px] text-slate-400">{i.zona}</div></td>
          <td className="px-3 py-1.5 text-slate-500">{i.unidad}</td>
          <td className="px-3 py-1.5 text-right font-semibold">{i.cantidad === null ? <span className="text-amber-600 font-normal">no contado</span> : fmtNum(i.cantidad)}</td>
          {showDiff && <td className="px-3 py-1.5 text-right text-slate-500">{fmtNum(i.cantidad_anterior) || '—'}</td>}
          {showDiff && <td className="px-3 py-1.5 text-right"><Diff v={i.diferencia} /></td>}
        </tr>
      ))}
    </tbody>
  </table>
);

const Acciones = ({ rec, run, onEdit }) => {
  const enviado = rec.estado !== 'borrador';
  return (
    <div className="flex flex-wrap gap-2">
      <Button disabled={!enviado} onClick={() => run('exportar')} className="bg-orange-500 hover:bg-orange-600" data-testid="detalle-exportar-btn">
        <Download className="w-4 h-4 mr-1" /> Exportar para Justo
      </Button>
      <Button variant="outline" disabled={!enviado || rec.estado === 'subido_justo'} onClick={() => run('subido')} data-testid="detalle-marcar-subido-btn">
        <CheckCircle2 className="w-4 h-4 mr-1" /> Marcar subido a Justo
      </Button>
      {rec.origen !== 'stock_tupu' && <Button variant="outline" onClick={() => onEdit(rec.id)} data-testid="detalle-editar-btn"><Pencil className="w-4 h-4 mr-1" /> {enviado ? 'Corregir' : 'Abrir'}</Button>}
      {enviado && rec.origen !== 'stock_tupu' && <Button variant="outline" onClick={() => run('reabrir')} data-testid="detalle-reabrir-btn"><RotateCcw className="w-4 h-4 mr-1" /> Reabrir</Button>}
      <Button variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => run('eliminar')} data-testid="detalle-eliminar-btn"><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
};

export const RecuentoDetalleDialog = ({ recuentoId, onClose, onChanged, onEdit }) => {
  const [rec, setRec] = useState(null);

  useEffect(() => {
    setRec(null);
    if (recuentoId) recuentosAPI.get(recuentoId).then((r) => setRec(r.data)).catch((e) => toast.error(errorMsg(e, 'Error')));
  }, [recuentoId]);

  const run = async (accion) => {
    try {
      if (accion === 'exportar') {
        const res = await recuentosAPI.exportar(rec.id);
        downloadBlob(res.data, filenameFromHeaders(res.headers, `recuento_${rec.lugar_codigo}.xlsx`));
        toast.success('Excel generado');
      } else if (accion === 'subido') {
        await recuentosAPI.marcarSubido(rec.id);
        toast.success('Marcado como subido a Justo');
      } else if (accion === 'reabrir') {
        if (!window.confirm('¿Reabrir el recuento? Volverá a borrador y el encargado podrá editarlo.')) return;
        await recuentosAPI.reabrir(rec.id);
        toast.success('Recuento reabierto');
      } else if (accion === 'eliminar') {
        if (!window.confirm('¿Eliminar este recuento definitivamente?')) return;
        await recuentosAPI.remove(rec.id);
        toast.success('Recuento eliminado');
        onChanged();
        return onClose();
      }
      const { data } = await recuentosAPI.get(rec.id);
      setRec(data);
      onChanged();
    } catch (e) {
      toast.error(errorMsg(e, 'Error en la acción'));
    }
  };

  return (
    <Dialog open={!!recuentoId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="recuento-detalle-dialog">
        <DialogHeader><DialogTitle>{rec ? rec.lugar_nombre : 'Cargando...'}</DialogTitle></DialogHeader>
        {rec && (
          <div className="space-y-3 overflow-hidden flex flex-col min-h-0">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ESTADOS[rec.estado]?.className}`} data-testid="detalle-estado">{ESTADOS[rec.estado]?.label}</span>
              <span>Creado: {formatFechaHora(rec.created_at)}</span>
              <span>Responsable: {rec.responsable || rec.user_name}</span>
              {rec.fecha_envio && <span>Enviado: {formatFechaHora(rec.fecha_envio)}</span>}
              {rec.fecha_exportacion && <span>Exportado: {formatFechaHora(rec.fecha_exportacion)}</span>}
              {rec.fecha_subido_justo && <span data-testid="detalle-fecha-subido">Subido a Justo: {formatFechaHora(rec.fecha_subido_justo)}</span>}
              <span>Contados: {rec.ingredientes_contados}/{rec.total_ingredientes}</span>
              <span>Anterior: {rec.recuento_anterior ? formatFechaHora(rec.recuento_anterior.created_at) : 'sin recuento previo'}</span>
            </div>
            {rec.comentario && <p className="text-sm bg-slate-50 rounded p-2">“{rec.comentario}”</p>}
            {rec.modificado_por && <p className="text-xs text-slate-400">Última modificación: {rec.modificado_por} · {formatFechaHora(rec.fecha_modificacion)}</p>}
            <Acciones rec={rec} run={run} onEdit={onEdit} />
            <div className="overflow-y-auto border rounded-lg min-h-0 flex-1"><ItemsTable items={rec.items} showDiff={rec.origen !== 'stock_tupu'} /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
